/* React Higher Order Component which fetches organization activity meta tag data for organization (props.orgId) which owns activity (props.activityId)
    Child (WrappedComponent) will only render once meta tag data is successfully fetched.
    child will receive props: {
        orgId:  PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        activityId:  PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        orgActMetaTags: PropTypes.object,
        actMetaTagCategories: PropTypes.object,
        orgActMetaTypes: PropTypes.array,
    }

*/

import React from 'react';
import PropTypes from 'prop-types';
const has = require('lodash/has');
const uuid = require('uuid');

const Globals = require('../../../../app/globals');
const FeatureUtil = require('../../../../app/shared/FeatureUtil');
const PermissionsUtil = require('../../../../app/shared/PermissionsUtil');
const ActivityActions = require('../../actions/ActivityActions');
const ActivityStore = require('../../stores/activity_store_reflux');
const ChannelStore = require('../../stores/channel_store_reflux');
const ProjectStore = require('../../stores/ProjectStore');
import LoadingMainContent from '../loading/LoadingMainContent';
import ErrorBox from '../common/ErrorBox';
const ReactUtil = require('../../util/ReactUtil');

const propTypes = {
    orgId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    activityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    renderBeforeLodingTags: PropTypes.bool,
    forceRefresh: PropTypes.bool,
};

const defaultProps = {
    renderBeforeLodingTags: false,
    forceRefresh: false,
};

export default function withOrganizationActivityMetaTagsViaActivity(WrappedComponent) {
    class WithOrganizationActivityMetaTagsViaActivity extends React.Component {
        constructor(props) {
            super(props);

            const actStoreState = ActivityStore.getState();

            this.state = {
                orgActMetaTags: actStoreState.activityMetaTags,
                actMetaTagCategories: actStoreState.activityMetaTagCategories,
                orgActMetaTypes: actStoreState.activityMetaTypes,
                getTagsActionId: null,
                fatalError: null
            };

            this.getOrgMetaActTagsAndTypes = this.getOrgMetaActTagsAndTypes.bind(this);
            this.isDataLoaded = this.isDataLoaded.bind(this);
            this.unsubscribes = [
                ActivityStore.listen(this.onActivityStoreChange.bind(this)),
                ChannelStore.listen(this.onChannelStoreChange.bind(this)),
                ProjectStore.listen(this.onProjectStoreChange.bind(this)),
            ];
        }

        componentDidMount() {
            this.getOrgMetaActTagsAndTypes(this.props.orgId, this.props.projectId, this.props.activityId, this.props.forceRefresh);
        }

        componentDidUpdate(prevProps) {
            if (
                prevProps.orgId !== this.props.orgId
                || prevProps.projectId !== this.props.projectId
                || prevProps.activityId !== this.props.activityId
            ) {
                this.getOrgMetaActTagsAndTypes(this.props.orgId, this.props.projectId, this.props.activityId, this.props.forceRefresh);
            }
        }

        componentWillUnmount() {
            this.unsubscribes.forEach(u => u());
        }

        isDataLoaded(orgId) {
            return (
                !!this.state.actMetaTagCategories
                && !!this.state.orgActMetaTags[orgId]
                && !!this.state.orgActMetaTypes[orgId]
            );
        }

        getOrgMetaActTagsAndTypes(orgId, projectId, activityId, forceRefresh=false) {
            const channelStoreState = ChannelStore.getStateChannel(orgId);
            const projectStoreState = ProjectStore.getStateProject(projectId);
            if (
                channelStoreState.initialized
                && FeatureUtil.hasActivityMetaTags(channelStoreState.channel.channel_permissions)
                && PermissionsUtil.canViewActMetaTagsForAct(channelStoreState.channel.user_permissions, projectStoreState.project.user_permissions)
            ) {
                if (
                    !forceRefresh
                    && this.isDataLoaded(orgId)
                ) { // if tags + categories & types are already populated do not refetch, unless forceRefresh is true
                    return;
                }

                const getTagsActionId = uuid.v4();
                const getTypeActionId = uuid.v4();
                this.setState({ getTagsActionId: getTagsActionId, getTypeActionId: getTypeActionId }, () => {
                    ActivityActions.getActMetaTagsForOrgViaAct(getTagsActionId, { orgId: orgId, actId: activityId });
                    ActivityActions.getActMetaTypesForOrgViaAct(getTypeActionId, { orgId: orgId, actId: activityId });
                });
            }
        }

        onActivityStoreChange(newState) {
            let additionalState = {};
            if (newState.action.id === this.state.getTagsActionId) {
                additionalState = { ...additionalState, getTagsActionId: null };

                if (!!newState.action.error) {
                    additionalState = { ...additionalState, fatalError: newState.action.error };
                }
            }

            if (newState.action.id === this.state.getTypeActionId) {
                additionalState = { ...additionalState, getTypeActionId: null };

                if (!!newState.action.error) {
                    additionalState = { ...additionalState, fatalError: newState.action.error };
                }
            }

            const actStoreState = ActivityStore.getState();
            this.setState({
                orgActMetaTags: actStoreState.activityMetaTags,
                actMetaTagCategories: actStoreState.activityMetaTagCategories,
                orgActMetaTypes: actStoreState.activityMetaTypes,
                ...additionalState
            });
        }

        onChannelStoreChange(newState) {
            this.getOrgMetaActTagsAndTypes(this.props.orgId, this.props.projectId, this.props.activityId);
        }

        onProjectStoreChange(newState) {
            this.getOrgMetaActTagsAndTypes(this.props.orgId, this.props.projectId, this.props.activityId);
        }

        render() {
            if (this.state.fatalError) {
                return <ErrorBox error={this.state.fatalError} allowStackTrace={true} />;
            }

            if (
                !this.isDataLoaded(this.props.orgId)
                && !this.props.renderBeforeLodingTags
            ) {
                return (
                    <div className="assignment-instructor">
                        <LoadingMainContent />
                    </div>
                );
            }

            return (
                <WrappedComponent
                    {...this.props}
                    orgActMetaTags={this.state.orgActMetaTags[this.props.orgId]}
                    actMetaTagCategories={this.state.actMetaTagCategories}
                    orgActMetaTypes={this.state.orgActMetaTypes[this.props.orgId]}
                />
            );
        }
    }

    WithOrganizationActivityMetaTagsViaActivity.propTypes = propTypes;
    WithOrganizationActivityMetaTagsViaActivity.defaultProps = defaultProps;
    WithOrganizationActivityMetaTagsViaActivity.displayName = `WithOrganizationActivityMetaTagsViaActivity(${ReactUtil.getComponentDisplayName(WrappedComponent)})`;
    return WithOrganizationActivityMetaTagsViaActivity;
}